import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import {
  CalendarDays,
  GraduationCap,
  Wallet,
  BookOpen,
  Clock3,
  Globe,
  ArrowRight,
  Download,
  DockIcon,
  FlaskConical,
  Handshake,
  Award,
  FileText,
  ClipboardCheck,
  Info,
  HeartPlus,
  BookCheck,
  Sparkles,
  ArrowUpRight,
  MapPin,
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";

import DataFetcher from "../../hooks/DataFetcherPosgrados";
import Breadcumbs from "../../components/Breadcumbs";
import Accordion from "../../components/Accordion";
import BannerGeneral from "../../components/BannerGeneral";
import ConectUs from "../../components/ConectUs";
import Quote from "../../components/Quote";
import Modalidad from "../../components/posgrados/Modalidad";
import Duracion from "../../components/posgrados/Duracion";
import Campo from "../../components/posgrados/Campo";
import BlockRightImage from "../../components/BlockRightImage";
import PosgradoPagos, { PagosEscuelaNegocios } from "../../components/PosgradoPagos";
import MasonryGallery from "../../components/MasonryGallery";
import LogoGallery from "../../components/LogoGallery";
import logoescuela from "../../img/iconsoferta/escuela-de-negocio.png";
import PerfilIngresoModal from "../../components/posgrados/PerfilIngresoModal";
import HeroBanner from "../../components/posgrados/HeroBanner";
import InfoCards from "../../components/posgrados/InfoCards";
import BlockDiferencial from "../../components/posgrados/BlockDiferencial";
import Malla from "../../components/posgrados/Malla";
import SubMenuAncla from "../../components/posgrados/SubMenuAncla";
const url =
  "https://be-ofertas.utpl.edu.ec/jsonapi/node/posgrado/90e177ba-8f65-426e-8f55-43d2b71f3876" +
  // 1. INCLUDES: Se define la ruta completa hacia el archivo físico de la imagen del docente
  "?include=field_imagen_portada.field_media_image," +
  "field_imagen_requisitos.field_media_image," +
  "field_imagen_perfil_de_egreso.field_media_image," +
  "field_imagen_perfil_ingreso.field_media_image," +
  "field_imagen_director.field_media_image," +
  "field_imagen1.field_media_image," +
  "field_gallery.field_media_image," +
  "field_nuestros_docentes.field_imagen_docente.field_media_image,field_brochure,field_archivo_malla_curricular" + // <-- Actualizado
  // 2. FIELDS [node--posgrado]: Campos del nodo principal (posgrado en singular)
  "&fields[node--posgrado]=title,field_titulo,field_modalidad,field_etiqueta_modalidad,field_precio," +
  "field_campo_amplio,field_duracion,field_inicio_clases,field_resolucion," +
  "field_diferencia_1,field_diferencia_2,field_diferencia_3," +
  "field_perfil_de_ingreso,field_perfil_de_ingreso_amplio,field_perfil_de_egreso,field_perfil_de_egreso_amplio,field_atributo_1,field_atributo_2,field_atributo_3," +
  "field_texto_director,field_imagen_director,field_nuestros_docentes,field_imagen1," +
  "field_imagen_portada,field_imagen_requisitos,field_requisitos_de_postulacion," +
  "field_malla_curricular,field_imagen_perfil_de_egreso,field_imagen_perfil_ingreso,field_texto_portada," +
  "field_publicado,field_enlace_postulacion,field_brochure,field_archivo_malla_curricular,field_bloque1,field_gallery" +
  // 3. FIELDS [node--docente]: Campos exactos que me indicaste
  "&fields[node--docente]=title,field_imagen_docente,field_contenido" +
  // 4. FIELDS de soporte para extraer la URI del archivo
  "&fields[media--image]=field_media_image" +
  "&fields[file--file]=uri";

export default function MaestriaAnalisisDatos() {
  const destinos = [
    {
      universidad: "EAFIT",
      ciudad: "Medellín, Colombia",
      descripcion:
        "Vive una semana en una ciudad llena de innovación. Conecta con empresas reales, descubre nuevas ideas y abre tu mente a una experiencia global.",
      badge: "Innovación & Co-creación",
      bgGradient: "from-blue-50 to-indigo-50",
    },
    {
      universidad: "EDEM",
      ciudad: "Valencia, España",
      descripcion:
        "Aprende marketing digital e inteligencia artificial con casos reales, mientras disfrutas una experiencia cultural única en Valencia.",
      badge: "Marketing Digital & IA",
      bgGradient: "from-amber-50 to-orange-50",
    },
  ];

  const alianzas = [
    {
      tipo: "Título Europeo",
      institucion: "Universidad Politécnica de Valencia",
      siglas: "UPV",
      descripcion:
        "Impulsa tu perfil profesional en el ecosistema educativo europeo con una de las universidades politécnicas de mayor prestigio y reconocimiento en España.",
      enlace: "https://utpl.edu.ec/alianza-upv",
      colorTheme: "from-sky-400 to-indigo-500",
      accentBg: "bg-sky-500/10 text-sky-300 border-sky-400/20",
      beneficios: [
        "Prestigio y calidad del Espacio Europeo de Educación Superior",
        "Conexión con redes de investigación y desarrollo global",
        "Acceso a metodologías europeas de innovación",
      ],
    },
  ];

  // Variantes para la animación de entrada de las tarjetas
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const DataDisplay = ({ data }) => {
    return (
      <div className="bg-white overflow-hidden mt-16">
        {/* HERO */}
        <HeroBanner
          image={data.imagen_portada}
          content={data.field_texto_portada}
          showApply={data.field_publicado}
          applyUrl={data.field_enlace_postulacion?.uri}
          brochure={data.brochure}
          logo={logoescuela}
        />
        {/* BREADCRUMBS */}
        <div className="border-b border-slate-200 bg-white">
          <div className="mb-6">
            <Breadcumbs
              content={
                <div className="flex items-center gap-2 text-sm">
                  <NavLink to="/escuela-negocios">Escuela de Negocios</NavLink>
                  <span>/</span>
                  <NavLink to="/maestria-datos">
                    Maestría en Análisis y Gestión de Datos para la Economía y
                    la Empresa
                  </NavLink>
                </div>
              }
            />
          </div>
        </div>
  {/* INFO CARDS DATA */}
        <InfoCards
          titulo={data.titulo_recibir}
          inversion={data.precio}
          inicio={data.inicio}
          duracion={
            <>
              <Duracion value={data.duracion}></Duracion>
            </>
          }
          campo={<Campo value={data.campo} />}
          modalidad={
            <>
              <Modalidad value={data.modalidad} />
              <div
                dangerouslySetInnerHTML={{
                  __html: data.etiqueta_modalidad,
                }}
              />
            </>
          }
          resolucion={
            <div
              dangerouslySetInnerHTML={{
                __html: data.resolucion,
              }}
            />
          }
        ></InfoCards> 

        

        {/* PERFIL INGRESO */}
        <section id="ingreso" className="py-24  ">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-20 items-start py-4">
            <motion.div {...fadeUp}>
              {data.info1 && (
                <div  >
                  <Quote
                    content={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: data.info1,
                        }}
                      />
                    }
                  ></Quote>{" "}
                </div>
              )}
              <div className="px-8 lg:px-0">
                <span className="text-gray-600  uppercase ">
                  Perfil académico
                </span>
                <div
                  className=" 
                [&_h2]:font-bold  "
                  dangerouslySetInnerHTML={{
                    __html: data.ingreso,
                  }}
                />
                {data.ingreso_amplio && (
                  <div className="mt-4">
                    <Accordion
                      title="Más información"
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.ingreso_amplio,
                          }}
                        />
                      }
                    />
                  </div>
                )}
                <PerfilIngresoModal></PerfilIngresoModal>
              </div>
            </motion.div>

            <motion.div {...fadeUp}>
              <img
                src={data.imagen_ingreso}
                alt="Perfil"
                className="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </section>

        {/* PERFIL EGRESO */}
        <section id="egreso" className="py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-20 items-start py-4">
            <motion.div {...fadeUp}>
              <img
                src={data.imagen_perfil}
                alt="Perfil"
                className="w-full h-[600px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>
            <motion.div {...fadeUp}>
              {data.info2 && (
                <div >
                  <Quote
                    content={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: data.info2,
                        }}
                      />
                    }
                  ></Quote>
                </div>
              )}
              <div className="px-8 lg:px-0">
                <span className=" text-gray-600   uppercase  ">
                  Perfil profesional
                </span>
                <div
                  className=" [&_h2]:font-bold   "
                  dangerouslySetInnerHTML={{
                    __html: data.egreso,
                  }}
                />
                {data.egreso_amplio && (
                  <div className="mt-4">
                    <Accordion
                      title="Más información"
                      content={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.egreso_amplio,
                          }}
                        />
                      }
                    />
                  </div>
                )}{" "}
              </div>
            </motion.div>
          </div>
        </section>

        {data.diferencia1 && (
          <BlockDiferencial
            diferencia1={
              <>
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10">
                  <Handshake className="text-amber-400" size={30} />
                </div>

                <div dangerouslySetInnerHTML={{ __html: data.diferencia1 }} />
              </>
            }
            diferencia2={
              <>
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10">
                  <Globe className="text-amber-400" size={30} />
                </div>
                <div dangerouslySetInnerHTML={{ __html: data.diferencia2 }} />
              </>
            }
            diferencia3={
              <>
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10">
                  <Award className="text-amber-400" size={30} />
                </div>
                <div dangerouslySetInnerHTML={{ __html: data.diferencia3 }} />
              </>
            }
          />
        )}

        {/* MALLA */}
        <section id="malla">
          <Malla
            malla={data.malla}
            mallaCurricular={data.malla_curricular}
          ></Malla>
        </section>
        {/* Quote */}
        <div className="max-w-6xl m-auto px-6">
          {data.info3 && (
            <div className="mt-10">
              <Quote
                content={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data.info3,
                    }}
                  />
                }
              />
            </div>
          )}
        </div>

         

        
        {/*SEMANAS INTERNACIONALES */}

        <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
          {/* Elementos decorativos de fondo sutiles */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-100/30 rounded-full blur-3xl -z-10" />

          <div className="max-w-5xl mx-auto">
            {/* Encabezado */}
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-[#004270] text-sm font-medium mb-4"
              >
                <Globe className="w-4 h-4" />
                <span>Programas de movilidad</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className=" font-bold  "
              >
                Semanas Internacionales
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto"
              >
                Elige tu destino y conviértelo en una experiencia que va más
                allá del aula{" "}
                <span className="inline-block animate-bounce">🚀</span>
              </motion.p>
            </div>

            {/* Contenedor de Destinos */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid gap-8 md:grid-cols-2"
            >
              {destinos.map((destino, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{ y: -6, transition: { duration: 0.2 } }}
                  className={`group relative flex flex-col justify-between p-8 rounded-2xl bg-gradient-to-br ${destino.bgGradient} border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}
                >
                  <div>
                    {/* Badge e Icono interactivo */}
                    <div className="flex justify-between items-start mb-6">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/80 backdrop-blur-sm text-xs font-semibold text-slate-700 shadow-sm border border-slate-100">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                        {destino.badge}
                      </span>

                      <div className="rounded-full bg-white p-2 text-slate-400 group-hover:text-slate-900 group-hover:rotate-45 transition-all duration-300 shadow-sm">
                        <ArrowUpRight className="w-5 h-5" />
                      </div>
                    </div>

                    {/* Títulos */}
                    <div className="mb-4">
                      <h3 className=" font-bold  ">
                        {destino.universidad}
                      </h3>
                      <div className="flex items-center gap-1 mt-1 text-sm font-medium text-slate-500">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>{destino.ciudad}</span>
                      </div>
                    </div>

                    {/* Descripción */}
                    <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                      {destino.descripcion}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* DIRECTOR */}
        <section className="py-24 bg-white">
          <div className="max-w-6xl mx-auto  grid lg:grid-cols-2 gap-20 items-center">
            <motion.div {...fadeUp}>
              <img
                src={data.imagen_director}
                alt="Director"
                className="w-auto h-[550px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div className="px-10 lg:px-0" {...fadeUp}>
              <span className="text-gray-600  uppercase  ">
                Director del programa
              </span>

              <div
                className="  mt-6 link-with-arrow-1 "
                dangerouslySetInnerHTML={{
                  __html: data.director,
                }}
              />
            </motion.div>
          </div>
        </section>

        {/* DOCENTES */}
        {data.nuestros_docentes && data.nuestros_docentes.length > 0 && (
          <section id="docentes" className="pt-10 pb-10">
            <div className="max-w-6xl mx-auto pb-10  ">
              <motion.div {...fadeUp} className="text-center mb-10">
                <h2 className="  font-bold">
                  Nuestros docentes
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
                {data.nuestros_docentes.map((profe, index) => (
                  <motion.div
                    key={profe.id}
                    {...fadeUp}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                    }}
                    whileHover={{
                      y: -10,
                    }}
                    className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-2xl transition-all"
                  >
                    <img
                      src={profe.foto}
                      alt={profe.nombre}
                      className="w-full h-80 object-cover"
                    />

                    <div className="px-8">
                      <h3 className="text-lg font-bold text-slate-800">
                        {profe.nombre}
                      </h3>

                      <div
                        className=" text-slate-600 link-with-arrow-1"
                        dangerouslySetInnerHTML={{
                          __html: profe.contenido,
                        }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
        {/*EN ALIANZA CON */}
        <section className=" text-center mb-10">
          <div
            className="pb-10"
            dangerouslySetInnerHTML={{
              __html: data.bloque1,
            }}
          />
          <LogoGallery gallery={data.galeria} />
        </section>

 
     {/*REQUISITOS */}
            <section
              id="requisitos"
              className="max-w-6xl mx-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="px-12">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-[#004270]">
                  <ClipboardCheck className="h-6 w-6" />
                </div>
                <h2 className="  font-bold  ">Requisitos de postulación</h2>
                <div
                  dangerouslySetInnerHTML={{
                    __html: data.requisitos,
                  }}
                />{" "}
              </div>
              {data.field_publicado && (
                <div>
                  <div className="flex mt-10 px-12">
                    <a
                      href={data.field_enlace_postulacion?.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-amber-400 hover:bg-blue-700 hover:text-white text-[#004270] px-8 py-4 rounded-2xl font-semibold transition-all flex items-center gap-2 shadow-2xl"
                    >
                      Postular ahora
                      <ArrowRight size={18} />
                    </a>
                  </div>
    
                  <div className="mt-10">
                    <PagosEscuelaNegocios> </PagosEscuelaNegocios>
                  </div>
                </div>
              )}
            </section>
      </div>
    );
  };

  return (
    <>

    <SubMenuAncla></SubMenuAncla>
      <DataFetcher url={url}>
        {(data) => <DataDisplay data={data} />}
      </DataFetcher>
      <div className="max-w-6xl m-auto">
        <div className="mt-8 rounded-2xl border border-sky-200 bg-sky-50 p-5 flex gap-3 items-start">
          <Info className="h-5 w-5 text-sky-600 mt-1" />

          <p className="text-slate-700 text-xs leading-7 p-0">
            Implementamos adaptaciones curriculares no significativas para
            garantizar experiencias de aprendizaje inclusivas y accesibles.
          </p>
        </div>
      </div>
      <div className="mt-10 py-8">
        <ConectUs
          instagram="https://www.instagram.com/utplescueladenegocios"
          linkedin="https://www.linkedin.com/company/utplescueladenegocios"
          tiktok="https://www.tiktok.com/@utplescueladenegocios"
          facebook="https://www.facebook.com/utplescuelanegocios/"
        ></ConectUs>
      </div>

      <BannerGeneral />
    </>
  );
}
